import './style/searchRankings.scss';

import IDate from '@dvrd/idate';

import React, {MouseEventHandler, useContext, useMemo} from 'react';

import {useTranslation} from 'react-i18next';
import {APIType, PeriodLabel, RankPositionFilter, SearchRankingSort} from '../../util/enums';

import {
    OrderDirection,
    SearchRankingProduct,
    SearchRankingsColumnDef,
    SearchRankingStats,
    StatCardData,
    TrackedKeyword
} from '../../util/interfaces';

import {PortalSelectOption, SearchRankingMode} from '../../util/types';

import BolmateStatCard from '../controls/statCard/bolmateStatCard';

import SearchRankingsControlBar from './controlBar/searchRankingsControlBar';

import PositionFilterBar from './positionFilterBar/positionFilterBar';

import SearchRankingsTable from './table/searchRankingsTable';

import ImportKeywordsDialog from './importKeywords/importKeywordsDialog';

import {AppContext} from '../misc/appContext';

import APIConnectionBanner from '../apiConnection/apiConnectionBanner';

import {OAUTH_SEARCH_RANKINGS_REDIRECT} from '../../util/constants';
import {BolmateTooltip} from '../controls';
import classNames from 'classnames';
import {AwesomeIcon} from '@dvrd/dvr-controls';
import InfoBanner from '../misc/infoBanner';

interface Props {
    products: Array<SearchRankingProduct>;
    stats: SearchRankingStats;
    prevStats: SearchRankingStats;
    loadingProducts: boolean;
    loadingStats: boolean;
    onSearchChange: (value: string) => void;
    rankingMode: SearchRankingMode;
    onRankingModeChange: (mode: SearchRankingMode) => void;
    activePositionFilter: RankPositionFilter;
    onPositionFilterChange: (filter: RankPositionFilter) => void;
    selectedCountries: Array<'NL' | 'BE'>;
    onCountryChange: (value: string) => void;
    selectedStoreIds: Array<string>;
    storeItems: Array<PortalSelectOption<string>>;
    onStoreChange: (value: string | null, selected: boolean, values?: Array<string>) => void;
    periodValue: [IDate | null, IDate | null];
    selectedPreset: PeriodLabel | null;
    onPeriodChange: (range: [IDate, IDate], preset: PeriodLabel | null) => void;
    expandedProductId: string | null;
    onToggleExpand: (ean: string) => void;
    sortField: SearchRankingSort | null;
    sortOrder: OrderDirection;
    onSort: (field: SearchRankingSort) => void;
    page: number;
    limit: number;
    totalRows: number;
    onPageChange: (page: number) => void;
    onLimitChange: (limit: number) => void;
    accountId: string;
    onKeywordsChanged: VoidFunction;
    columns: Array<SearchRankingsColumnDef>;
    onExportExcel: VoidFunction;
    exportingExcel: boolean;
    visibleColumnFields: Array<string>;
    allColumnItems: Array<PortalSelectOption>;
    onColumnsChange: (value: string | null, selected: boolean, values?: Array<string>) => void;
    importDialogActive: boolean;
    onImportClick: VoidFunction;
    onImportClose: VoidFunction;
    onAddKeyword: (
        ean: string,
        keyword: string,
        callback: (keyword: TrackedKeyword, restored: boolean) => void
    ) => void;
    onUpgradeLimit: MouseEventHandler;
    manualKeywordsCount: number;
    manualKeywordsLimit: number | null;
}

const EMPTY_KPI: StatCardData = {
    label: '\u2014',
    value: 0,
    prevValue: 0
};

type KPICard = StatCardData & {tooltip?: string};

function computeDelta(current: number, prev: number | null | undefined): Pick<StatCardData, 'prevValue' | 'value'> {
    const noData: Pick<StatCardData, 'prevValue' | 'value'> = {
        prevValue: 0,
        value: 0
    };
    if (prev == null) return noData;
    if (current === 0 && prev === 0) return noData;
    return {value: current, prevValue: prev};
}

function isEmptyStats(s: SearchRankingStats): boolean {
    return (
        s.avg_rank === 0 &&
        s.best_rank === 0 &&
        s.total_search_volume === 0 &&
        s.visibility_score === 0 &&
        s.total_keywords === 0
    );
}

function buildKpiCards(
    stats: SearchRankingStats | null,
    prevStats: SearchRankingStats | null,
    t: (k: string, f: string) => string
): Array<KPICard> {
    if (!stats || isEmptyStats(stats)) {
        return [
            {
                ...EMPTY_KPI,
                label: t('searchRankings.kpi.avgRank', 'Gem. zoekpositie'),
                alternate: {...EMPTY_KPI, label: t('searchRankings.kpi.bestRank', 'Beste zoekpositie')}
            },
            {
                ...EMPTY_KPI,
                label: t('searchRankings.kpi.keywordsTop3', 'Keywords in Top 3'),
                alternate: {...EMPTY_KPI, label: t('searchRankings.kpi.keywordsTop10', 'Keywords in Top 10')}
            },
            {...EMPTY_KPI, label: t('searchRankings.kpi.totalSearchVolume', 'Totaal zoekvolume')},
            {
                ...EMPTY_KPI,
                label: t('searchRankings.kpi.visibility', 'Zichtbaarheid'),
                valueType: 'percentage',
                tooltip: t('searchRankings.kpi.visibilityTooltip', 'Percentage van producten zichtbaar in de top 10')
            },
            {...EMPTY_KPI, label: t('searchRankings.kpi.totalKeywords', 'Totaal keywords')}
        ];
    }

    const avgRankDelta = computeDelta(stats.avg_rank, prevStats?.avg_rank);
    const bestRankDelta = computeDelta(stats.best_rank, prevStats?.best_rank);
    const top3Delta = computeDelta(stats.position_counts.TOP_3, prevStats?.position_counts.TOP_3);
    const top10Delta = computeDelta(stats.position_counts.TOP_10, prevStats?.position_counts.TOP_10);
    const volumeDelta = computeDelta(stats.total_search_volume, prevStats?.total_search_volume);
    const visibilityDelta = computeDelta(stats.visibility_score, prevStats?.visibility_score);
    const keywordsDelta = computeDelta(stats.total_keywords, prevStats?.total_keywords);
    const categoriesDelta = computeDelta(stats.total_categories, prevStats?.total_categories);

    return [
        {
            label: t('searchRankings.kpi.avgRank', 'Gem. zoekpositie'),
            invertTrend: true,
            ...avgRankDelta,
            alternate: {
                label: t('searchRankings.kpi.bestRank', 'Beste zoekpositie'),
                invertTrend: true,
                ...bestRankDelta
            }
        },
        {
            label: t('searchRankings.kpi.keywordsTop3', 'Keywords in Top 3'),
            ...top3Delta,
            alternate: {
                label: t('searchRankings.kpi.keywordsTop10', 'Keywords in Top 10'),
                ...top10Delta
            }
        },
        {
            label: t('searchRankings.kpi.totalSearchVolume', 'Totaal zoekvolume'),
            truncateThreshold: 10000,
            ...volumeDelta
        },
        {
            label: t('searchRankings.kpi.visibility', 'Zichtbaarheid'),
            valueType: 'percentage',
            tooltip: t('searchRankings.kpi.visibilityTooltip', 'Percentage van producten zichtbaar in de top 10'),
            ...visibilityDelta
        },
        {
            label: t('searchRankings.kpi.totalKeywords', 'Totaal keywords'),
            ...keywordsDelta,
            alternate: {
                label: t('searchRankings.kpi.totalCategories', 'Totaal categorieën'),
                ...categoriesDelta
            }
        }
    ];
}

export default function SearchRankings(props: Props) {
    const {t} = useTranslation();
    const {account} = useContext(AppContext).accountContext;
    const {
        products,
        stats,
        prevStats,
        loadingProducts,
        onSearchChange,
        rankingMode,
        onRankingModeChange,
        activePositionFilter,
        onPositionFilterChange,
        selectedCountries,
        onCountryChange,
        selectedStoreIds,
        storeItems,
        onStoreChange,
        periodValue,
        selectedPreset,
        onPeriodChange,
        expandedProductId,
        onToggleExpand,
        sortField,
        sortOrder,
        onSort,
        page,
        limit,
        totalRows,
        onPageChange,
        accountId,
        onKeywordsChanged,
        columns,
        onExportExcel,
        exportingExcel,
        visibleColumnFields,
        allColumnItems,
        onColumnsChange,
        importDialogActive,
        onImportClick,
        onImportClose,
        onAddKeyword,
        onUpgradeLimit,
        manualKeywordsCount,
        manualKeywordsLimit
    } = props;

    const kpis = useMemo(() => buildKpiCards(stats, prevStats, t), [stats, prevStats, t]);

    function renderAPIBanner() {
        if (!account) return null;
        const apiKey = account.getActiveAPIKey();
        if (apiKey?.enabledForAPIType(APIType.ADVERTISER)) return null;
        return (
            <APIConnectionBanner
                oauthState={OAUTH_SEARCH_RANKINGS_REDIRECT}
                className='api-banner'
                title={t('searchRankings.apiBannerTitle', 'Welkom bij Search Rankings!')}
            >
                <p>
                    {t(
                        'searchRankings.apiBanner',
                        'Bekijk hier hoe hoog jouw producten in de zoekresultaten verschijnen.\nTrack de keywords van Bol en voeg zelf jouw belangrijkste keywords toe.\n\nVernieuw eerst jouw koppeling met Bol:'
                    )}
                </p>
            </APIConnectionBanner>
        );
    }

    function renderLimitIndicator() {
        const elem = (
            <div
                className={classNames('account-limit', manualKeywordsLimit !== null && 'upgradable')}
                onClick={onUpgradeLimit}
            >
                <span className='gray'>Keywords:</span>
                <span className='current-count'>{manualKeywordsCount}</span>
                <span className='gray'>/ {manualKeywordsLimit ?? '\u221E'}</span>
                {manualKeywordsLimit !== null && (
                    <AwesomeIcon name='circle-up' prefix='far' className='upgrade-icon gray' />
                )}
            </div>
        );
        if (manualKeywordsLimit === null) return elem;
        return (
            <BolmateTooltip text={t('searchRankings.upgradeForHigherLimit', 'Upgrade voor hoger limiet')}>
                {elem}
            </BolmateTooltip>
        );
    }

    return (
        <div className='search-rankings'>
            <InfoBanner featureKey='search_rankings' className='feature-info'>
                {t(
                    'searchRankings.banner.line1',
                    'Volg hier de posities van jouw producten in de zoekopdrachten op bol. De posities worden elke dag bijgehouden.'
                )}
                <br />
                <br />
                {t('searchRankings.banner.line2', 'Voeg eerst zelf de keywords toe die jij wilt tracken.')}
                <br />
                {t('searchRankings.banner.line3', 'Wij geven suggesties per product.')}
            </InfoBanner>

            {/* KPI Tiles */}
            <div className='search-rankings-kpi-grid'>
                {kpis.map((kpi, idx) => {
                    return <BolmateStatCard key={idx} {...kpi} />;
                })}
            </div>

            <div className='positions-row'>
                {/* Position Filters */}
                <PositionFilterBar
                    positionCounts={stats.position_counts}
                    prevPositionCounts={prevStats.position_counts}
                    activeFilter={activePositionFilter}
                    onFilterChange={onPositionFilterChange}
                />

                {/* Account limit */}
                {renderLimitIndicator()}
            </div>

            {/* Control Bar */}
            <SearchRankingsControlBar
                onSearchChange={onSearchChange}
                rankingMode={rankingMode}
                onRankingModeChange={onRankingModeChange}
                selectedCountries={selectedCountries}
                onCountryChange={onCountryChange}
                selectedStoreIds={selectedStoreIds}
                storeItems={storeItems}
                onStoreChange={onStoreChange}
                periodValue={periodValue}
                selectedPreset={selectedPreset}
                onPeriodChange={onPeriodChange}
                onExportExcel={onExportExcel}
                exportingExcel={exportingExcel}
                visibleColumnFields={visibleColumnFields}
                allColumnItems={allColumnItems}
                onColumnsChange={onColumnsChange}
                onImportClick={onImportClick}
                manualKeywordsCount={manualKeywordsCount}
                manualKeywordsLimit={manualKeywordsLimit}
            />

            {/* Products Table */}
            <SearchRankingsTable
                products={products}
                loading={loadingProducts}
                rankingMode={rankingMode}
                expandedProductId={expandedProductId}
                onToggleExpand={onToggleExpand}
                sortField={sortField}
                sortOrder={sortOrder}
                onSort={onSort}
                page={page}
                limit={limit}
                totalRows={totalRows}
                onPageChange={onPageChange}
                onLimitChange={props.onLimitChange}
                accountId={accountId}
                countryCodes={selectedCountries}
                onImportClick={onImportClick}
                onAddKeyword={onAddKeyword}
                columns={columns}
                onKeywordsChanged={onKeywordsChanged}
                manualKeywordCount={manualKeywordsCount}
                manualKeywordLimit={manualKeywordsLimit}
            />

            {renderAPIBanner()}

            <ImportKeywordsDialog
                active={importDialogActive}
                onClose={onImportClose}
                accountId={accountId}
                onSuccess={onKeywordsChanged}
            />
        </div>
    );
}
